import { useIntl } from '@capsicum/common';
import { type StaticImageData } from 'next/image';

import { type FanTokenOfferingDrawerParams, FanTokenOfferingLimits } from '../models';

type UseFanTokenOfferingDrawerDataProps = {
	drawerParams: FanTokenOfferingDrawerParams;
	image?: any | StaticImageData;
	testID: string;
	type: FanTokenOfferingLimits;
};

// use any to avoid error on mobile generic drawer props.
type UseFanTokenOfferingDrawerDataReturnType = {
	buttonText: string;
	description: string;
	image: any;
	testID: string;
	title: string;
};

/*
 * Generate the props for the drawer depending on the type of error/user limit
 *
 * @param {StaticImageData} image - the goat image to show
 * @param {FanTokenOfferingLimits} type - the type of limit
 * @param {FanTokenOfferingDrawerParams} drawerParams - params used to generate translations
 *
 * @returns {UseFanTokenOfferingDrawerDataReturnType} list of props to send to the drawer
 */
export const useFanTokenOfferingDrawerData = ({
	drawerParams,
	image,
	testID = 'fto',
	type
}: UseFanTokenOfferingDrawerDataProps): UseFanTokenOfferingDrawerDataReturnType => {
	const { $t } = useIntl();

	switch (type) {
		case FanTokenOfferingLimits.OverCap:
			return {
				buttonText: $t('fanTokenOffering.errorDrawer.overcap.buttonText'),
				description: $t('fanTokenOffering.errorDrawer.overcap.description'),
				image,
				testID: `${testID}-ftoDrawerOverCap`,
				title: $t('fanTokenOffering.errorDrawer.overcap.title')
			};
		case FanTokenOfferingLimits.InsufficientBalance:
			return {
				buttonText: $t('fanTokenOffering.errorDrawer.insufficientBalance.buttonText'),
				description: $t('fanTokenOffering.errorDrawer.insufficientBalance.description', {
					amount: drawerParams.capPerUser,
					currency: drawerParams.fanTokenShortCode
				}),
				image,
				testID: `${testID}-ftoDrawerInsufficientBalance`,
				title: $t('fanTokenOffering.errorDrawer.insufficientBalance.title', {
					currency: drawerParams.fanTokenShortCode
				})
			};
		default:
			return {
				buttonText: $t('fanTokenOffering.errorDrawer.default.buttonText'),
				description: $t('fanTokenOffering.errorDrawer.default.description'),
				image,
				testID: `${testID}-ftoDrawerDefaultError`,
				title: $t('fanTokenOffering.errorDrawer.default.title')
			};
	}
};
